import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

const describeFinding = (finding) =>
  [finding.code, finding.message].filter(Boolean).join(' ')

const TIP_GAP = 4
const TIP_ARROW = 6
const TIP_HALF_WIDTH = 128
// A ::before tooltip cannot scroll, so a long list is cut short instead of running off the screen.
const TIP_MAX_LINES = 8
const TIP_MAX_LINE_LENGTH = 120

const truncateLine = (line) => {
  const characters = [...line]
  return characters.length > TIP_MAX_LINE_LENGTH
    ? `${characters.slice(0, TIP_MAX_LINE_LENGTH - 1).join('')}…`
    : line
}

// Pins the tooltip to the viewport so scrolling tables and sticky headers cannot clip it.
const placeTooltip = (icon) => {
  const rect = icon.getBoundingClientRect()
  const center = rect.left + rect.width / 2
  const isAbove = rect.top > window.innerHeight / 2
  const boxX = Math.min(
    Math.max(center, TIP_HALF_WIDTH),
    window.innerWidth - TIP_HALF_WIDTH
  )
  const arrowY = isAbove
    ? rect.top - TIP_GAP - TIP_ARROW * 2
    : rect.bottom + TIP_GAP
  const boxY = isAbove
    ? rect.top - TIP_GAP - TIP_ARROW + 1
    : rect.bottom + TIP_GAP + TIP_ARROW - 1

  icon.dataset.tipPlacement = isAbove ? 'top' : 'bottom'
  icon.style.setProperty('--tip-box-x', `${boxX}px`)
  icon.style.setProperty('--tip-box-y', `${boxY}px`)
  icon.style.setProperty('--tip-arrow-x', `${center - TIP_ARROW}px`)
  icon.style.setProperty('--tip-arrow-y', `${arrowY}px`)
}

// Red when any finding is an error, amber when all are warnings; the tooltip lists every finding.
export default function FindingsBadge({ findings, showCount = false }) {
  const iconRef = useRef(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const isOpen = isHovered || isFocused

  // The fixed coordinates go stale once the page scrolls, so the tooltip hides until the next hover or focus.
  useEffect(() => {
    const icon = iconRef.current
    if (!isOpen || !icon) return undefined
    const hide = () => icon.classList.add('is-tip-hidden')
    const hideOnEscape = (event) => {
      if (event.key === 'Escape') hide()
    }
    window.addEventListener('scroll', hide, true)
    window.addEventListener('keydown', hideOnEscape)
    return () => {
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('keydown', hideOnEscape)
    }
  }, [isOpen])

  if (findings.length === 0) return null

  const show = () => {
    placeTooltip(iconRef.current)
    iconRef.current.classList.remove('is-tip-hidden')
  }
  const errorCount = findings.filter(
    (finding) => finding.severity === 'error'
  ).length
  const warningCount = findings.length - errorCount
  const counts = [
    errorCount > 0 ? plural(errorCount, 'error') : null,
    warningCount > 0 ? plural(warningCount, 'warning') : null,
  ].filter(Boolean)
  const lines = findings.map(describeFinding)
  const tipLines =
    lines.length > TIP_MAX_LINES
      ? [
          ...lines.slice(0, TIP_MAX_LINES).map(truncateLine),
          `+${lines.length - TIP_MAX_LINES} more`,
        ]
      : lines.map(truncateLine)
  const isError = errorCount > 0

  return (
    <span
      ref={iconRef}
      className={[
        'lox-blocks-explorer__finding-icon',
        isError ? 'is-error' : 'is-warning',
        'has-tooltip-arrow has-tooltip-multiline lox-no-border',
        isError ? 'has-tooltip-danger' : 'has-tooltip-warning',
      ].join(' ')}
      role="img"
      aria-label={`${counts.join(', ')}: ${lines.join(' ')}`}
      data-tooltip={`${counts.join(', ')}\n${tipLines.join('\n')}`}
      // Focusable so keyboard users can open the findings tooltip too.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      onMouseEnter={() => {
        show()
        setIsHovered(true)
      }}
      onFocus={() => {
        show()
        setIsFocused(true)
      }}
      onMouseLeave={() => setIsHovered(false)}
      onBlur={() => setIsFocused(false)}
    >
      <FontAwesomeIcon icon={faTriangleExclamation} />
      {showCount && ` ${findings.length}`}
    </span>
  )
}
